import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Bus, Ticket, QrCode, BarChart3, Wallet, ShieldCheck, ArrowRight, Sun, Moon, Check } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";

const HERO_IMG = "https://images.pexels.com/photos/39080480/pexels-photo-39080480.jpeg";
const FEAT_IMG = "https://images.pexels.com/photos/31293033/pexels-photo-31293033.jpeg";

const plans = [
  { name: "FREE", price: "0", label: "Gratuit", desc: "Testez notre plateforme", features: ["1 utilisateur", "50 billets/mois", "QR Code", "Mode clair/sombre"] },
  { name: "BASIC", price: "10 000", label: "Basique", desc: "Petites agences", features: ["3 staff", "500 billets/mois", "Trajets & tarifs", "Caisse", "Rapports"] },
  { name: "PRO", price: "15 000", label: "Professionnel", desc: "Activité importante", features: ["10 staff", "Billets illimités", "Comptabilité", "Analyses avancées", "Export PDF/Excel"], highlight: true },
  { name: "BUSINESS", price: "25 000", label: "Business", desc: "Grandes agences", features: ["Staff illimité", "Multi-agences", "Multi-devises", "API", "Sauvegardes auto"] },
];

export default function Landing() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <header className="glass sticky top-0 z-30 h-16 flex items-center px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <img src="https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png" alt="Transport.pro" className="w-10 h-10 rounded-xl object-cover" />
          <span className="font-display font-bold text-lg">Transport.pro</span>
        </Link>
        <nav className="ml-auto flex items-center gap-2">
          <a href="#features" className="hidden md:inline-block text-sm text-slate-600 dark:text-slate-300 px-3 py-2 hover:text-orange-500">Fonctionnalités</a>
          <a href="#plans" className="hidden md:inline-block text-sm text-slate-600 dark:text-slate-300 px-3 py-2 hover:text-orange-500">Tarifs</a>
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="landing-theme-btn">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          {user ? (
            <Link to="/app" data-testid="cta-goto-app">
              <Button className="btn-pill bg-orange-500 hover:bg-orange-600">Ouvrir l'app <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link"><Button variant="ghost" className="btn-pill">Connexion</Button></Link>
              <Link to="/register" data-testid="header-register-link"><Button className="btn-pill bg-orange-500 hover:bg-orange-600">Commencer</Button></Link>
            </>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/60 to-slate-50 dark:to-slate-950" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-32 md:pt-32 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block px-3 py-1 rounded-full bg-orange-500/20 text-orange-300 text-xs font-semibold tracking-widest uppercase mb-6">Nouveau · SaaS billetterie transport</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            La billetterie moderne pour votre agence de transport
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-6 text-lg text-white/85 max-w-xl">
            Réservations, billets temps réel, QR Codes, caisse et statistiques — tout dans un seul espace pensé pour l'Afrique de l'Ouest.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="hero-cta-register">
              <Button size="lg" className="btn-pill bg-orange-500 hover:bg-orange-600 text-white h-12 px-8 text-base">
                Créer mon agence gratuitement <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#plans" data-testid="hero-cta-plans">
              <Button size="lg" variant="secondary" className="btn-pill h-12 px-8 text-base bg-white/10 backdrop-blur border-white/20 text-white hover:bg-white/20">
                Voir les tarifs
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold">Tout ce dont votre agence a besoin</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400">Une plateforme complète, multi-entreprises, avec rôles Créateur / Admin / Staff. Adaptée aux gares, points de vente et agents mobiles.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Ticket, t: "Billet de réservation & temps réel" },
                { icon: QrCode, t: "QR Code sécurisé & vérifiable" },
                { icon: Wallet, t: "Caisse & gestion des espèces" },
                { icon: BarChart3, t: "Statistiques & rapports" },
                { icon: ShieldCheck, t: "Sécurité multi-tenant" },
                { icon: Bus, t: "Trajets, tarifs, horaires" },
              ].map(({ icon: I, t }) => (
                <div key={t} className="flex items-start gap-3 p-4 card-elev hover:shadow-md transition-shadow">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-500/20 text-orange-500 grid place-items-center shrink-0"><I className="w-4 h-4" /></div>
                  <div className="text-sm font-medium">{t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
            <img src={FEAT_IMG} alt="Gare routière" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/50 via-transparent to-orange-500/30" />
            <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/85 dark:bg-slate-900/85 rounded-2xl p-4 border border-white/50 dark:border-slate-800">
              <div className="text-xs uppercase tracking-widest text-orange-500 font-semibold">Bamako → Sikasso</div>
              <div className="font-display text-xl font-bold mt-1">7 500 FCFA</div>
              <div className="text-xs text-slate-500 mt-1">4 horaires · 50 places · Départ 08:00</div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold tracking-widest uppercase text-orange-500">Abonnements</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Choisissez votre plan</h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">Tous les prix en FCFA. Changement, upgrade et downgrade à tout moment.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((p) => (
              <div key={p.name} data-testid={`plan-card-${p.name.toLowerCase()}`}
                className={`p-6 rounded-2xl border transition-transform hover:-translate-y-1 ${
                  p.highlight
                    ? "bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-500/20"
                    : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                }`}>
                <div className="text-xs font-semibold tracking-widest uppercase opacity-75">{p.label}</div>
                <div className="mt-2 font-display font-bold text-2xl">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display font-bold text-4xl">{p.price}</span>
                  <span className="text-sm opacity-80">FCFA/mois</span>
                </div>
                <p className="text-sm opacity-80 mt-2">{p.desc}</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <Link to="/register" className="block mt-6">
                  <Button className={`btn-pill w-full ${p.highlight ? "bg-white text-orange-600 hover:bg-orange-50" : "bg-orange-500 hover:bg-orange-600 text-white"}`}>
                    Commencer
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-slate-500 flex flex-col md:flex-row justify-between gap-4">
        <div>© 2026 Transport.pro — SaaS Billetterie Transport</div>
        <div className="flex gap-4">
          <Link to="/login">Connexion</Link>
          <Link to="/register">Inscription</Link>
        </div>
      </footer>
    </div>
  );
}
